import { useCallback, useEffect, useRef, useState } from "react"

import Button from "@material-ui/core/Button"
import IconButton from "@material-ui/core/IconButton"
import { Close, Print } from "@material-ui/icons"

import useAppStore from "../stores/AppStore"
import { autoAceiteLigado } from "../stores/ConfigsStore"
import { forcarImpressaoNavegador } from "../stores/ImpressaoStore"
import { getIntervaloRedeSegurancaMs } from "../stores/PedidosStore"
import { ABA_ID, anunciarPedidoAtualizado } from "../stores/canalPedidos"
import { buscarInformacoesPedido } from "../services/integracao"
import {
    useStreamPedidosCompartilhado,
    souLiderPedidos,
    liderancaCompartilhadaSuportada,
    publicarEventoPedido,
} from "../pages/PedidosDoDia/useStreamPedidosCompartilhado"
import { verificarPrintQRCode } from "../pages/RecebedorDePedidos/functions"
import RingAudio from "../pages/RecebedorDePedidos/audios/ring.mp3"
import { useSnackbar } from "../utils/notificacoes"

// Canal entre abas do mesmo navegador. É o MESMO nome usado pelo stream SSE
// (useStreamPedidosCompartilhado): lá trafegam mensagens { tipo: "evento" }; aqui
// adicionamos { tipo: "silenciar" }. Cada aba tem seu próprio <audio id="audiopedido">
// e toca de forma independente, então um clique na bandeja de UMA tela precisa avisar
// as demais para todas pararem o som ao mesmo tempo.
const CANAL_EVENTOS = "pedidos-sse"

// Id único desta ABA (não do navegador). Usado para a líder ignorar o eco do próprio
// snapshot do allday que ela repassa pelo BroadcastChannel — evitando reconciliar duas
// vezes (uma no fetch local, outra ao receber a própria mensagem por outro objeto BC).
const TAB_ID =
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`

// Canal de envio persistente (não recebe as próprias mensagens; por isso o
// silenciarAvisoTodasAbas também para o som localmente). Reutilizado entre cliques
// para não vazar um BroadcastChannel a cada acionamento.
let canalSilenciar = null
function getCanalSilenciar() {
    if (typeof BroadcastChannel === "undefined") return null
    if (!canalSilenciar) {
        try {
            canalSilenciar = new BroadcastChannel(CANAL_EVENTOS)
        } catch {
            canalSilenciar = null
        }
    }
    return canalSilenciar
}

/**
 * Para o som de novo pedido em TODAS as abas do navegador (a atual e as demais).
 * Chamado no clique da bandeja do Cabeçalho, que existe em toda tela.
 */
export function silenciarAvisoTodasAbas() {
    // Para já nesta aba — cobre também o navegador sem BroadcastChannel.
    try {
        useAppStore.getState().desativarAviso()
    } catch {
        /* ignore */
    }
    const bc = getCanalSilenciar()
    if (bc) {
        try {
            bc.postMessage({ tipo: "silenciar" })
        } catch {
            /* ignore */
        }
    }
}

/**
 * Núcleo NÃO-VISUAL da operação em tempo real, comum a todas as telas:
 *
 *  - assina o SSE (aceite/impressão automática no modo "Sempre" e som de novo pedido);
 *  - liga a rede de segurança global (allday a cada 1 min) contra eventos perdidos;
 *  - pré-conecta o QZ no modo automático;
 *  - mantém o <audio> do aviso de novo pedido.
 *
 * Isso vivia só dentro do Cabeçalho — que está em todas as telas MENOS no Registrador
 * de Pedidos, onde ele só é montado com vários estabelecimentos ou no mobile. Com o
 * Registrador aberto sozinho na aba (caso comum: 1 estabelecimento, desktop), nada
 * assinava o SSE nem rodava a rede de segurança: o pedido não era aceito nem impresso
 * e acabava EXPIRANDO. Por isso o núcleo virou este componente headless, que o
 * Registrador também monta.
 *
 * Montar duas vezes na mesma aba é seguro (o Recebedor já assina o SSE junto com o
 * Cabeçalho): a conexão SSE é única por navegador (Web Lock) e a impressão dedupica
 * por `SolicitacaoImpressao.id`.
 */

// Rede de segurança GLOBAL contra eventos SSE perdidos: um único intervalo (allday a
// cada 1 min) que reconcilia as listas e aceita/imprime os pedidos que o push ao vivo
// deixou passar. Fica em ESCOPO DE MÓDULO (não num useEffect) de propósito: o
// componente remonta a cada troca de rota, então um timer preso ao ciclo de vida
// reiniciaria a cada navegação e quase nunca dispararia. A ação no store se
// auto-guarda (só roda em modo operação com sessão ativa), então o intervalo pode
// viver a sessão toda.
let intervaloRedeSeguranca = null
function garantirRedeSegurancaGlobal() {
    if (intervaloRedeSeguranca) return
    intervaloRedeSeguranca = setInterval(async () => {
        try {
            // Só a aba LÍDER faz o polling do allday — 1 chamada de rede por navegador,
            // não uma por aba aberta. As demais recebem o snapshot pelo BroadcastChannel
            // (handler abaixo) e reconciliam sem refazer a rede.
            if (!souLiderPedidos()) return
            const pedidos = await useAppStore.getState().redeSegurancaPedidos()
            if (Array.isArray(pedidos) && liderancaCompartilhadaSuportada()) {
                const bc = getCanalSilenciar()
                if (bc) {
                    try {
                        bc.postMessage({ tipo: "allday", pedidos, origem: TAB_ID })
                    } catch {
                        /* ignore */
                    }
                }
            }
        } catch (e) {
            /* best-effort — a rede não pode derrubar a UI */
        }

        // Rede de segurança da IMPRESSÃO, no mesmo tick (sem timer novo).
        //
        // O allday cobre o ESTADO DA LISTA, não a fila de impressão: PRINT1..n (setor),
        // CUPOM_FISCAL e comandas de rodada de mesa são linhas de SolicitacaoImpressao e
        // não existem no payload dele. Um pedido pode estar perfeitamente atualizado na
        // tela e ter uma comanda de setor que nunca saiu.
        await recuperarImpressoesPendentes()
    }, getIntervaloRedeSegurancaMs())
}

/**
 * Busca o que ficou pendente de impressão e injeta no roteador de eventos do SSE.
 *
 * Só a aba líder busca; a entrega é broadcast porque a líder pode ser uma aba passiva
 * (Registrador, VIEW) onde processarEventoImpressao dá early-return — quem imprime é a
 * aba em modo CRUD, que talvez não seja a líder.
 *
 * Não dá ACK: o endpoint é somente leitura e as integrações seguem com a fila intacta.
 * O dedup por SolicitacaoImpressao.id é o que impede a via duplicada.
 */
async function recuperarImpressoesPendentes() {
    try {
        if (!souLiderPedidos()) return
        if (useAppStore.getState().tabStatus === "GARCOM") return

        const pendentes = await useAppStore.getState().redeSegurancaImpressao()
        if (!Array.isArray(pendentes) || pendentes.length === 0) return

        for (const evento of pendentes) {
            publicarEventoPedido(evento)
        }
    } catch (e) {
        /* best-effort — a rede não pode derrubar a UI */
    }
}

// Catch-up na REABERTURA do SSE: durante uma queda, todo push é perdido (pub/sub não tem
// replay). Sem isto, a recuperação só viria no próximo tick de 1 min. O debounce evita
// martelar o backend durante um flapping de rede.
const DEBOUNCE_CATCHUP_MS = 15000
let ultimoCatchupEm = 0
function catchUpAoReabrirSse() {
    const agora = Date.now()
    if (agora - ultimoCatchupEm < DEBOUNCE_CATCHUP_MS) return
    ultimoCatchupEm = agora
    recuperarImpressoesPendentes()
}

// Atualização IMEDIATA da lista a partir do evento LEVE `{tipo:"PLACED"}`.
//
// Um pedido novo chega em DUAS mensagens no SSE: a leve ({pedidoId, status, tipo:"PLACED"})
// — que toca o som — e a pesada (kind:"IMPRESSAO", code:"PLACED"/"PRINT") — que insere o
// pedido na lista via upsertPedidoNasListas. Como o push não tem replay, se a leve chega
// (som toca) mas a pesada se perde, a lista só aparecia no allday da rede de segurança
// (~1 min). Aqui acoplamos a lista ao evento CONFIÁVEL: no PLACED leve, a aba líder busca
// o pedido pelo id e reconcilia a exibição na hora, em todas as abas — sem esperar o minuto.
//
// Só a LÍDER busca (1 chamada de rede por navegador) e faz broadcast; as demais abas
// reconciliam pelo canal (handler `pedido-atualizado`). Idempotente com o evento pesado:
// upsertPedidoNasListas reconcilia por id/status, então um upsert duplo é inofensivo.
const ultimoRefreshPlaced = new Map()
const JANELA_REFRESH_PLACED_MS = 10000
async function atualizarListaPorPlacedLeve(pedidoId) {
    if (pedidoId == null) return
    try {
        if (!souLiderPedidos()) return
        if (useAppStore.getState().tabStatus === "GARCOM") return

        // Já está na lista? Então o evento pesado chegou e inseriu — nada a fazer. Limita a
        // chamada de rede EXATAMENTE ao caso que estamos corrigindo (pesado perdido).
        const s = useAppStore.getState()
        const jaExibido = (s.arrayTodosPedidos || []).some(
            p => String(p?.id) === String(pedidoId)
        )
        if (jaExibido) return

        // Dedup curto: a mesma leve pode ser reentregue; evita refetch em rajada.
        const agora = Date.now()
        const anterior = ultimoRefreshPlaced.get(String(pedidoId))
        if (anterior != null && agora - anterior < JANELA_REFRESH_PLACED_MS) return
        ultimoRefreshPlaced.set(String(pedidoId), agora)
        for (const [k, t] of ultimoRefreshPlaced) {
            if (agora - t > JANELA_REFRESH_PLACED_MS) ultimoRefreshPlaced.delete(k)
        }

        const autorizacao = localStorage.getItem("chaveSistema")
        const pedido = await buscarInformacoesPedido(autorizacao, pedidoId)
        if (!pedido?.id || pedido.status < 0) return

        // Reconcilia NESTA aba (a líder pode ser a própria tela de operação) e avisa as
        // demais para exibirem na hora.
        useAppStore.getState().upsertPedidoNasListas(pedido)
        anunciarPedidoAtualizado(pedido)
    } catch (e) {
        // A leve já tocou o som; a rede de segurança (allday) ainda cobre a lista se isto
        // falhar. Nunca deixa a UI cair por causa do refresh.
        ultimoRefreshPlaced.delete(String(pedidoId))
    }
}

// Reconciliação IMEDIATA da lista a partir do evento LEVE `{tipo:"STATUS"}` — troca de
// status de um pedido que JÁ está na lista (tipicamente feita por OUTRA estação).
//
// Antes isto era feito pelo handler da própria tela do Recebedor (reconciliarPedido), o
// que fazia CADA aba com o Painel aberto buscar o pedido por conta própria — N abas = N
// buscas por evento. Aqui é leader-only (1 busca por navegador) e o resultado é repassado
// às demais abas pelo BroadcastChannel (handler `pedido-atualizado`), igual ao PLACED.
//
// Diferente do PLACED, NÃO há guarda `jaExibido` nem dedup por id: o pedido já está na
// lista, mas o status MUDOU — é justamente a atualização que queremos aplicar. Cada evento
// STATUS é uma transição real; buscá-lo no líder é o comportamento correto.
async function reconciliarStatusPorEventoLeve(pedidoId) {
    if (pedidoId == null) return
    try {
        if (!souLiderPedidos()) return
        if (useAppStore.getState().tabStatus === "GARCOM") return

        const autorizacao = localStorage.getItem("chaveSistema")
        const pedido = await buscarInformacoesPedido(autorizacao, pedidoId)
        if (!pedido?.id) return

        // Reconcilia NESTA aba (a líder pode ser a própria tela) e repassa às demais.
        useAppStore.getState().upsertPedidoNasListas(pedido)
        anunciarPedidoAtualizado(pedido)
    } catch (e) {
        // A rede de segurança (allday) ainda cobre a lista se isto falhar. Nunca deixa a
        // UI cair por causa do refresh.
    }
}

// Avisos de impressão pendente já mostrados NESTA ABA. Em escopo de módulo (não num
// useRef) porque a mesma aba pode ter duas instâncias do hook (Cabeçalho + Registrador
// no mobile) — com um Set por instância, o mesmo pedido geraria dois avisos.
const avisosImpressaoMostrados = new Set()

/**
 * Garante UM `<audio id="audiopedido">` por aba, criado direto no body.
 *
 * Era um elemento JSX do Cabeçalho, e por isso sumia junto com ele: no Registrador de
 * uma conta com vários estabelecimentos, o Cabeçalho vive dentro do `ParteInicial`, que
 * DESMONTA assim que o operador entra no carrinho — dali em diante `ativarAviso` não
 * achava o elemento e a aba ficava muda para pedidos novos. Fora do React ele não
 * depende de qual tela está montada, e sendo por id não há como duplicar (dois
 * elementos com o mesmo id fariam o getElementById apontar sempre para o primeiro).
 */
function garantirAudioAviso() {
    if (typeof document === "undefined") return
    if (document.getElementById("audiopedido")) return
    const audio = document.createElement("audio")
    audio.id = "audiopedido"
    audio.src = RingAudio
    audio.loop = true
    audio.style.display = "none"
    document.body.appendChild(audio)
}

/**
 * Efeitos da operação em tempo real, sem nada visual. O Cabeçalho chama o hook direto
 * (aproveita o `qzAtivo` de retorno nos avisos de impressão); quem não monta o
 * Cabeçalho usa o componente headless do fim do arquivo.
 */
export function useOperacaoTempoReal({ onNovoPedido } = {}) {
    const configs = useAppStore(s => s.configuracoes)
    const tabStatus = useAppStore(s => s.tabStatus)
    const setTabStatus = useAppStore(s => s.setTabStatus)

    const emVisualizacao = tabStatus === "VIEW"
    // Modo GARÇOM: terminal de lançamento puro (celular). NÃO assina SSE, não toca, não faz
    // polling (rede de segurança/QZ), não pré-conecta QZ e não imprime. O pedido vai ao
    // backend por REST e a estação da loja imprime pela SSE dela.
    const emModoGarcom = tabStatus === "GARCOM"
    const aceiteAutomatico =
        configs?.recebedor?.aceiteAutomatico ??
        (configs?.recebedor?.aceitarPedidoAutomatico ? "TELA_ABERTA" : "DESLIGADO")
    const emModoAutomatico = aceiteAutomatico === "SEMPRE" || aceiteAutomatico === "TELA_ABERTA"
    // QZ "configurado" = escolheu impressão via QZ (não forçou o navegador) e selecionou
    // ao menos uma impressora. É a configuração persistente, não o websocket deste
    // instante.
    const qzConfigurado =
        !forcarImpressaoNavegador() && (configs?.printer?.impressoras?.length ?? 0) > 0
    const isElectron = typeof navigator !== "undefined" && navigator.userAgent.includes("Electron")

    // Roteia os eventos SSE globais:
    //  - Evento de IMPRESSÃO (push da comanda): com o aceite automático LIGADO, imprime/
    //    aceita a partir do próprio evento, em qualquer tela. NÃO dá ACK (não consome a
    //    fila do servidor), para não "roubar" o evento das integrações por polling. O
    //    dedup por SolicitacaoImpressao.id evita duplicar com o Recebedor.
    //    (O antigo modo TELA_ABERTA foi descontinuado e passa a se comportar igual ao
    //    SEMPRE — por isso o gate usa `autoAceiteLigado`, não `=== "SEMPRE"`.)
    //  - Evento leve "PLACED": toca o som de novo pedido e avisa quem montou o
    //    componente (o Cabeçalho usa para balançar a bandeja). "STATUS" não.
    const aoEventoPedido = useCallback(
        evento => {
            // Defesa extra: no modo Garçom esta aba nem assina o SSE, mas ainda escuta o
            // BroadcastChannel; ignora qualquer evento repassado por outra aba.
            if (useAppStore.getState().tabStatus === "GARCOM") return
            if (evento?.kind === "IMPRESSAO") {
                const store = useAppStore.getState()
                if (autoAceiteLigado(store.configuracoes?.recebedor))
                    store.processarEventoImpressao(evento)
                return
            }
            if (evento?.tipo === "PLACED") {
                onNovoPedido?.()
                useAppStore.getState().ativarAviso()
                // Acopla a atualização da lista ao evento CONFIÁVEL (a leve, que fez o som
                // tocar): sem isto, se a mensagem pesada se perdesse no push, o pedido só
                // aparecia no allday (~1 min). A líder busca e reconcilia na hora.
                atualizarListaPorPlacedLeve(evento.pedidoId ?? evento.correlationId)
            } else if (evento?.tipo === "STATUS") {
                // Troca de status (multi-estação): a líder busca e reconcilia; repassa às
                // demais abas. Antes era o handler da tela do Recebedor que fazia isso, por
                // aba — aqui centraliza numa busca só por navegador. Não toca o som (STATUS
                // não é novo pedido).
                reconciliarStatusPorEventoLeve(evento.pedidoId ?? evento.correlationId)
            }
        },
        [onNovoPedido]
    )

    const autorizacaoSSE = localStorage.getItem("chaveSistema")
    // Stream da conta inteira (modo login): o sino e o aceite/impressão automático
    // valem para qualquer estabelecimento do fornecedor, então não filtra.
    // No modo Garçom passamos `undefined`: o hook dá early-return (não abre EventSource
    // nem participa da eleição de líder) — o celular do garçom não entra no SSE.
    useStreamPedidosCompartilhado(
        emModoGarcom ? undefined : autorizacaoSSE,
        aoEventoPedido,
        catchUpAoReabrirSse
    )

    useEffect(() => {
        // Garçom não faz o polling da rede de segurança (allday).
        if (emModoGarcom) return
        garantirRedeSegurancaGlobal()
    }, [emModoGarcom])

    // Ouve o pedido de silêncio das outras abas: quando a bandeja é clicada em qualquer
    // tela, aquela aba envia { tipo: "silenciar" } e todas param o próprio <audio>.
    // (O BroadcastChannel não entrega a mensagem à própria aba emissora — por isso o
    // silenciarAvisoTodasAbas também para o som localmente.)
    useEffect(() => {
        if (typeof BroadcastChannel === "undefined") return
        const bc = new BroadcastChannel(CANAL_EVENTOS)
        bc.onmessage = e => {
            const msg = e?.data
            if (!msg) return
            if (msg.tipo === "silenciar") {
                try {
                    useAppStore.getState().desativarAviso()
                } catch {
                    /* ignore */
                }
            } else if (
                msg.tipo === "allday" &&
                msg.origem !== TAB_ID &&
                Array.isArray(msg.pedidos)
            ) {
                // Snapshot do allday repassado pela aba LÍDER: reconcilia a exibição desta
                // aba (inclusive a tela do Recebedor, mesmo sem ser a líder) sem refazer a
                // chamada de rede. Ignora o próprio eco (origem === TAB_ID) para a líder
                // não reconciliar em duplicidade.
                try {
                    useAppStore.getState().reconciliarAllday(msg.pedidos)
                } catch {
                    /* ignore */
                }
            } else if (
                msg.tipo === "pedido-atualizado" &&
                msg.origem !== ABA_ID &&
                msg.pedido?.id
            ) {
                // Outra aba deste navegador mudou o status deste pedido (aceitar/preparar/
                // despachar/finalizar/cancelar). Reconcilia a exibição AGORA — sem esperar o
                // allday (~1 min) e sem recarregar a tela. Idempotente por id e respeita
                // status oculto. O terminal Garçom não exibe listas de pedido: ignora.
                try {
                    if (useAppStore.getState().tabStatus !== "GARCOM")
                        useAppStore.getState().upsertPedidoNasListas(msg.pedido)
                } catch {
                    /* ignore */
                }
            }
        }
        return () => {
            try {
                bc.close()
            } catch {
                /* ignore */
            }
        }
    }, [])

    // Padrão: com o aceite automático ligado, a operação inicia em modo operação (sai de
    // Visualização), COM OU SEM QZ — o aceite é feito pelo backend e, sem QZ, a comanda cai
    // na impressão pelo navegador. Roda uma única vez, após as configs carregarem, para não
    // sobrescrever uma troca manual posterior do usuário.
    //
    // Vive aqui (e não só no Cabeçalho) porque `tabStatus` é PERSISTIDO no localStorage e
    // vale para o navegador inteiro: uma aba que ficou em "Visualização" (o Recebedor
    // força VIEW no mobile) deixava o Registrador — que não monta o Cabeçalho, nem tem UI
    // de modo — preso em VIEW. Nesse estado a rede de segurança nem roda (exige CRUD) e
    // nada é impresso: o pedido expira sem sinal nenhum na tela.
    const modoPadraoAplicado = useRef(false)
    useEffect(() => {
        if (modoPadraoAplicado.current) return
        if (!configs) return
        modoPadraoAplicado.current = true
        // Auto-ativação do modo GARÇOM: em celular SEM QZ, o aparelho é um terminal de
        // lançamento (não recebe/imprime). Só migra a partir do default passivo (VIEW) e só
        // se o operador ainda não escolheu um modo manualmente nesta estação (marca
        // `modoOperacaoManual`). O default síncrono do TabStatusStore já cobre o 1º load;
        // isto migra celulares que tinham VIEW persistido de antes do recurso.
        const ehMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )
        let escolheuManual = false
        try {
            escolheuManual = localStorage.getItem("modoOperacaoManual") === "1"
        } catch {
            escolheuManual = false
        }
        if (ehMobile && !qzConfigurado && !escolheuManual && tabStatus === "VIEW") {
            setTabStatus("GARCOM")
            return
        }
        // Aceite automático ligado + aba em Visualização → promove para Operação (CRUD),
        // sem exigir QZ. O celular SEM QZ já foi desviado para GARÇOM acima (terminal de
        // lançamento), então isto não força uma estação-lançador a virar recebedora.
        if (emModoAutomatico && emVisualizacao) {
            setTabStatus("CRUD")
        }
    }, [configs, qzConfigurado, emModoAutomatico, emVisualizacao, tabStatus, setTabStatus])

    // Com aceite automático ligado, pré-conecta o QZ fora do Recebedor para que a
    // impressão automática já saia na primeira comanda em qualquer tela. O connect()
    // rejeita quando o QZ não está presente — tratamos para não gerar unhandled rejection.
    // (TELA_ABERTA foi descontinuado e se comporta igual ao SEMPRE, então o gate usa
    // `autoAceiteLigado`, não `=== "SEMPRE"`.)
    useEffect(() => {
        if (emModoGarcom) return
        if (autoAceiteLigado(configs?.recebedor))
            Promise.resolve(useAppStore.getState().buscarImpressoras()).catch(() => {})
        // O gatilho efetivo é o modo de aceite (string estável); autoAceiteLigado só lê
        // esse campo (+ o legado aceitarPedidoAutomatico) do recebedor.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configs?.recebedor?.aceiteAutomatico, emModoGarcom])

    // Estado do QZ + manutenção da conexão. O polling do `isActive()` roda SEMPRE (para
    // saber se o QZ está conectado em qualquer modo) e a reconexão acontece a cada ~30s
    // fora do Electron, enquanto houver modo automático ou impressora configurada.
    //
    // Também vive aqui porque estava só no Cabeçalho: no Registrador o QZ era conectado
    // uma única vez, no mount. Se o QZ Tray caísse ou reiniciasse durante o turno, aquela
    // aba ficava sem QZ para sempre e a impressão dela caía no navegador.
    const [qzAtivo, setQzAtivo] = useState(false)
    useEffect(() => {
        // Garçom não usa QZ: não pollar nem tentar reconectar.
        if (emModoGarcom) {
            setQzAtivo(false)
            return
        }
        let cancelado = false
        let ciclos = 0
        const tentarConectar = () => {
            // buscarImpressoras() chama qz.websocket.connect(), que REJEITA quando o
            // módulo não está presente. Engolimos a rejeição (o alerta já comunica ao
            // usuário) para não gerar unhandled rejection.
            Promise.resolve()
                .then(() => useAppStore.getState().buscarImpressoras())
                .catch(() => {})
        }
        const verificar = () => {
            // Usa o qzAtivo() do store (confiável): o qz.websocket.isActive() cru
            // considera CONNECTING como ativo, então com o QZ fechado (em reconexão)
            // ele mentia "ativo" e a lista de impressoras nunca era limpa.
            let ativo = false
            try {
                ativo = useAppStore.getState().qzAtivo()
            } catch {
                ativo = false
            }
            if (!cancelado) setQzAtivo(ativo)
            // QZ caiu (ou nunca conectou): zera a lista VIVA de impressoras do QZ. Sem
            // isso ela ficava "presa" com o valor de quando o QZ estava aberto, e o
            // `tipoPainelImpressao` continuava "plugin" — o clique em imprimir tentava o
            // QZ (falha silenciosa) em vez de cair para a impressão pelo navegador.
            if (!ativo && useAppStore.getState().impressorasQZ.length > 0) {
                useAppStore.getState().setState({ impressorasQZ: [] })
            }
            if (
                !ativo &&
                !isElectron &&
                (emModoAutomatico || qzConfigurado) &&
                ciclos % 6 === 0
            )
                tentarConectar()
            ciclos++
        }
        verificar()
        const timer = setInterval(verificar, 5000)
        return () => {
            cancelado = true
            clearInterval(timer)
        }
    }, [emModoAutomatico, isElectron, qzConfigurado, emModoGarcom])

    // Impressão pelo NAVEGADOR (sem QZ): o diálogo não abre mais sozinho. Quando um
    // pedido fica pronto, ele entra na fila `pedidosPendentesImpressao` e aqui mostramos
    // uma faixa com botão "Imprimir" — a comanda só sai quando o usuário clica. Isso
    // evita o diálogo do navegador travando a operação (drivers como o PDFCreator
    // prendiam a pré-visualização e congelavam a tela). Vive aqui (e não só no Cabeçalho)
    // para que a faixa apareça também no Registrador: sem QZ, ela é o ÚNICO caminho da
    // comanda — sem isso o pedido era aceito e a comanda ficava presa na fila, invisível.
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const pedidosPendentesImpressao = useAppStore(s => s.pedidosPendentesImpressao)
    const removerImpressaoPendente = useAppStore(s => s.removerImpressaoPendente)
    useEffect(() => {
        // Garçom não imprime: nunca mostra a faixa "clique para imprimir".
        if (emModoGarcom) return
        ;(pedidosPendentesImpressao || []).forEach(pedido => {
            if (avisosImpressaoMostrados.has(pedido.id)) return
            avisosImpressaoMostrados.add(pedido.id)

            const chave = `imprimir-pendente-${pedido.id}`
            const identificacao = pedido.shortReference ?? pedido.id
            const imprimirAgora = () => {
                const store = useAppStore.getState()
                store.mandarImprimir({
                    pedido,
                    texto: pedido.texto,
                    qtd: store.configuracoes?.user?.parametros?.viasImpressao,
                    qrcode: verificarPrintQRCode(pedido),
                    forcarNavegador: true,
                })
                removerImpressaoPendente(pedido.id)
                closeSnackbar(chave)
            }
            const dispensar = () => {
                removerImpressaoPendente(pedido.id)
                closeSnackbar(chave)
            }

            enqueueSnackbar(`Pedido #${identificacao} pronto — clique para imprimir`, {
                key: chave,
                persist: true,
                variant: "info",
                action: (
                    <>
                        <Button
                            size="small"
                            startIcon={<Print />}
                            onClick={imprimirAgora}
                            style={{ color: "#fff", fontWeight: 700 }}
                        >
                            Imprimir
                        </Button>
                        <IconButton size="small" onClick={dispensar}>
                            <Close fontSize="small" htmlColor="#fff" />
                        </IconButton>
                    </>
                ),
            })
        })
        // mandarImprimir/remover/closeSnackbar são estáveis; o gatilho é a fila mudar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pedidosPendentesImpressao, emModoGarcom])

    // Desbloqueio do áudio de novo pedido: navegadores bloqueiam play() programático
    // até o primeiro gesto do usuário. No primeiro clique/tecla, tocamos o áudio (mudo)
    // e pausamos — isso libera o autoplay para os toques seguintes (ativarAviso).
    useEffect(() => {
        // Garçom não toca som de novo pedido: não cria o <audio> nem os listeners.
        if (emModoGarcom) return
        garantirAudioAviso()
        const desbloquearAudio = () => {
            const audio = document.getElementById("audiopedido")
            if (audio) {
                const eraMudo = audio.muted
                audio.muted = true
                Promise.resolve(audio.play?.())
                    .then(() => {
                        audio.pause()
                        audio.currentTime = 0
                        audio.muted = eraMudo
                    })
                    .catch(() => {
                        audio.muted = eraMudo
                    })
            }
            window.removeEventListener("pointerdown", desbloquearAudio, true)
            window.removeEventListener("keydown", desbloquearAudio, true)
        }
        window.addEventListener("pointerdown", desbloquearAudio, true)
        window.addEventListener("keydown", desbloquearAudio, true)
        return () => {
            window.removeEventListener("pointerdown", desbloquearAudio, true)
            window.removeEventListener("keydown", desbloquearAudio, true)
        }
    }, [emModoGarcom])

    // O Cabeçalho usa isto para os avisos de impressão ("módulo não encontrado").
    return { qzAtivo }
}

/**
 * Versão montável do núcleo, para as telas que não têm Cabeçalho (Registrador, e o
 * Gerenciador de Mesas dentro de iframe). Não renderiza nada.
 */
export default function OperacaoTempoReal({ onNovoPedido }) {
    useOperacaoTempoReal({ onNovoPedido })
    return null
}
